import capitalize from 'lodash/capitalize';
import { FormattedMessage } from 'react-intl';
import React, { FC } from 'react';
import debounce from 'lodash/debounce';
import { useAnalytics } from 'src/hooks/useAnalytics/useAnalytics';
import useGTAG, { GTAGPrefix } from 'src/hooks/useGTAG';
import trns from '../../page-components/drug/trns';
import CartCountInput from '../../page-components/drug/components/CartCountInput';
import { DrugItem, DrugQuantityType } from '../../api/types';

interface Props {
  drug: DrugItem;
  packQuantity: number;
  unitQuantity: number;
  gtagPrefix?: GTAGPrefix;
  onQuantityChange: (
    packQuantity: number,
    unitQuantity: number,
    drugQuantityType?: DrugQuantityType
  ) => Promise<void>;
}
const UnitCounter: FC<Props> = ({
  drug,
  packQuantity,
  unitQuantity,
  gtagPrefix,
  onQuantityChange
}) => {
  const { sendChangeCartItemsAnalytic } = useAnalytics();
  const { trackGtagEvent } = useGTAG();

  const handleQuantityIncrease = (type: DrugQuantityType) =>
    debounce((value) => {
      let data = { packQuantity, unitQuantity };
      if (type === DrugQuantityType.Pack) {
        data = {
          packQuantity: value !== undefined ? value : packQuantity + 1,
          unitQuantity: unitQuantity || 0
        };
        trackGtagEvent(`${gtagPrefix}_pack_plus`);
      }

      if (type === DrugQuantityType.Unit) {
        const quantity = value !== undefined ? value : unitQuantity + 1;
        if (
          value !== undefined ||
          unitQuantity + 1 === drug.quantityUnitInPack
        ) {
          const additionalPacks = Math.trunc(
            quantity / drug.quantityUnitInPack
          );
          data = {
            packQuantity: packQuantity + additionalPacks,
            unitQuantity: quantity - additionalPacks * drug.quantityUnitInPack
          };
        } else {
          data = {
            packQuantity,
            unitQuantity: quantity
          };
        }
        trackGtagEvent(`${gtagPrefix}_packtype_plus`);
      }

      onQuantityChange(data.packQuantity, data.unitQuantity, type);
      setTimeout(() => {
        sendChangeCartItemsAnalytic({
          drug,
          packageQuantityDetails: {
            packQuantity: data.packQuantity,
            unitQuantity: data.unitQuantity,
            quantityUnitInPack: drug.quantityUnitInPack
          }
        });
      });
    }, 100);

  const handleQuantityDecrease = (type: DrugQuantityType) =>
    debounce(() => {
      const shouldPackQuantityDecrease =
        type === DrugQuantityType.Pack &&
        packQuantity !== 0 &&
        !(packQuantity === 1 && unitQuantity === 0);

      const shouldUnitQuantityDecrease =
        type === DrugQuantityType.Unit &&
        unitQuantity !== 0 &&
        !(unitQuantity === 1 && packQuantity === 0);

      if (shouldPackQuantityDecrease) {
        onQuantityChange(packQuantity - 1, unitQuantity || 0);
        setTimeout(() => {
          sendChangeCartItemsAnalytic({
            drug,
            packageQuantityDetails: {
              packQuantity: 1
            },
            isRemove: true
          });
          trackGtagEvent(`${gtagPrefix}_pack_minus`);
        });
      }

      if (shouldUnitQuantityDecrease) {
        onQuantityChange(packQuantity, unitQuantity - 1);
        setTimeout(() => {
          sendChangeCartItemsAnalytic({
            drug,
            packageQuantityDetails: {
              packQuantity: 0,
              unitQuantity: 1,
              quantityUnitInPack: drug.quantityUnitInPack
            },
            isRemove: true
          });
          trackGtagEvent(`${gtagPrefix}_packtype_minus`);
        });
      }
    }, 100);

  return (
    <div className="relative flex w-full items-center">
      <div className="mr-4">
        <div className="rounded-l-md outline-none text-mako fs-body-reg h-full mb-2.5">
          <FormattedMessage {...trns.packaging} />
        </div>
        <div className="h-full z-10 flex rounded-lg border bg-white border-alto">
          <CartCountInput
            minCountOfItems={unitQuantity === 0 ? 1 : 0}
            quantity={{ packQuantity, unitQuantity }}
            countOfItems={packQuantity}
            plusClickCallback={handleQuantityIncrease(DrugQuantityType.Pack)}
            minusClickCallback={handleQuantityDecrease(DrugQuantityType.Pack)}
          />
        </div>
      </div>
      {drug?.quantityUnitInPack > 1 && drug?.unitName && (
        <div>
          <div className="outline-none text-mako fs-body-reg h-full mb-2.5">
            {capitalize(drug.unitName)}
          </div>
          <div className="h-full z-10 flex rounded-lg border bg-white border-alto">
            <CartCountInput
              minCountOfItems={packQuantity === 0 ? 1 : 0}
              quantity={{ packQuantity, unitQuantity }}
              countOfItems={unitQuantity}
              plusClickCallback={handleQuantityIncrease(DrugQuantityType.Unit)}
              minusClickCallback={handleQuantityDecrease(DrugQuantityType.Unit)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default UnitCounter;
